<template>
  <div class="amazon-region-connect">
    <div v-if="showNickname" class="sk-field sk-seller-nickname">
      <label for="seller-account-nickname">Storefront nickname <span class="sk-optional">(optional)</span></label>
      <input
        id="seller-account-nickname"
        type="text"
        v-model="accName"
        placeholder="e.g. US main account"
        autocomplete="off"
      />
    </div>

    <p
      v-if="showPricingMessage && $root.newPricingMessage"
      class="sk-pricing-note"
    >
      Introducing New Daily ASIN tracking! <strong>{{ $root.newPricingMessage }}</strong>
    </p>

    <div v-if="errorMsg" class="sk-error-msg" role="alert">{{ errorMsg }}</div>

    <div v-if="showIntro" class="sk-connect-intro">
      <h3>How it works</h3>
      <p>Pick a marketplace (or <strong>All</strong> for every country in that region), then authorize. Amazon opens a secure page; once you approve, SentryKit loads your store and ASINs automatically.</p>
    </div>

    <div class="sk-region-grid">
      <div class="sk-region-card" :class="{ 'is-busy': connectingRegion === 'NA' }">
        <div class="sk-region-card-top">
          <span class="sk-region-flag">🌎</span>
          <div class="sk-region-info">
            <div class="sk-region-name">Americas</div>
            <div class="sk-region-countries">US, Canada, Mexico, Brazil</div>
          </div>
        </div>
        <div class="sk-mkt-chips" role="group" aria-label="Americas marketplace">
          <button
            type="button"
            class="sk-mkt-chip sk-mkt-chip-all"
            data-region="NA"
            :data-marketplace-id="allMarketplacesSelection"
            :class="{ 'is-selected': isMarketplaceSelected('NA', allMarketplacesSelection) }"
            title="All Americas marketplaces"
            :aria-pressed="isMarketplaceSelected('NA', allMarketplacesSelection) ? 'true' : 'false'"
            :disabled="connectingRegion !== null"
            @mousedown.stop.prevent="selectMarketplace('NA', allMarketplacesSelection)"
            @click.stop.prevent="selectMarketplace('NA', allMarketplacesSelection)"
          >
            All
          </button>
          <button
            v-for="opt in naMarketplaces"
            :key="opt.marketplaceId"
            type="button"
            class="sk-mkt-chip"
            data-region="NA"
            :data-marketplace-id="opt.marketplaceId"
            :class="{ 'is-selected': isMarketplaceSelected('NA', opt.marketplaceId) }"
            :title="opt.label"
            :aria-pressed="isMarketplaceSelected('NA', opt.marketplaceId) ? 'true' : 'false'"
            :disabled="connectingRegion !== null"
            @mousedown.stop.prevent="selectMarketplace('NA', opt.marketplaceId)"
            @click.stop.prevent="selectMarketplace('NA', opt.marketplaceId)"
          >
            {{ opt.code }}
          </button>
        </div>
        <p v-if="selectedMarketplaceLabel('NA')" class="sk-region-selected-hint">
          Selected: {{ selectedMarketplaceLabel('NA') }} — then click Authorize →
        </p>
        <div class="sk-region-card-actions">
          <button
            type="button"
            class="sk-region-authorize"
            :disabled="connectingRegion !== null"
            @click.stop.prevent="connectRegion('NA', selectedMarketplaceNa)"
          >
            {{ connectingRegion === 'NA' ? 'Opening Amazon…' : 'Authorize →' }}
          </button>
        </div>
      </div>

      <div class="sk-region-card" :class="{ 'is-busy': connectingRegion === 'EU' }">
        <div class="sk-region-card-top">
          <span class="sk-region-flag">🇪🇺</span>
          <div class="sk-region-info">
            <div class="sk-region-name">Europe</div>
            <div class="sk-region-countries">UK, Germany, France, Spain, Italy, Netherlands, Belgium, Sweden, Poland, South Africa</div>
          </div>
        </div>
        <div class="sk-mkt-chips" role="group" aria-label="Europe marketplace">
          <button
            type="button"
            class="sk-mkt-chip sk-mkt-chip-all"
            data-region="EU"
            :data-marketplace-id="allMarketplacesSelection"
            :class="{ 'is-selected': isMarketplaceSelected('EU', allMarketplacesSelection) }"
            title="All Europe marketplaces"
            :aria-pressed="isMarketplaceSelected('EU', allMarketplacesSelection) ? 'true' : 'false'"
            :disabled="connectingRegion !== null"
            @mousedown.stop.prevent="selectMarketplace('EU', allMarketplacesSelection)"
            @click.stop.prevent="selectMarketplace('EU', allMarketplacesSelection)"
          >
            All
          </button>
          <button
            v-for="opt in euMarketplaces"
            :key="opt.marketplaceId"
            type="button"
            class="sk-mkt-chip"
            data-region="EU"
            :data-marketplace-id="opt.marketplaceId"
            :class="{ 'is-selected': isMarketplaceSelected('EU', opt.marketplaceId) }"
            :title="opt.label"
            :aria-pressed="isMarketplaceSelected('EU', opt.marketplaceId) ? 'true' : 'false'"
            :disabled="connectingRegion !== null"
            @mousedown.stop.prevent="selectMarketplace('EU', opt.marketplaceId)"
            @click.stop.prevent="selectMarketplace('EU', opt.marketplaceId)"
          >
            {{ opt.code }}
          </button>
        </div>
        <p v-if="selectedMarketplaceLabel('EU')" class="sk-region-selected-hint">
          Selected: {{ selectedMarketplaceLabel('EU') }} — then click Authorize →
        </p>
        <div class="sk-region-card-actions">
          <button
            type="button"
            class="sk-region-authorize"
            :disabled="connectingRegion !== null"
            @click.stop.prevent="connectRegion('EU', selectedMarketplaceEu)"
          >
            {{ connectingRegion === 'EU' ? 'Opening Amazon…' : 'Authorize →' }}
          </button>
        </div>
      </div>

      <div class="sk-region-card" :class="{ 'is-busy': connectingRegion === 'ME' }">
        <div class="sk-region-card-top">
          <span class="sk-region-flag">🌐</span>
          <div class="sk-region-info">
            <div class="sk-region-name">Middle East</div>
            <div class="sk-region-countries">UAE, Saudi Arabia, Egypt, Turkey</div>
          </div>
        </div>
        <div class="sk-mkt-chips" role="group" aria-label="Middle East marketplace">
          <button
            type="button"
            class="sk-mkt-chip sk-mkt-chip-all"
            data-region="ME"
            :data-marketplace-id="allMarketplacesSelection"
            :class="{ 'is-selected': isMarketplaceSelected('ME', allMarketplacesSelection) }"
            title="All Middle East marketplaces"
            :aria-pressed="isMarketplaceSelected('ME', allMarketplacesSelection) ? 'true' : 'false'"
            :disabled="connectingRegion !== null"
            @mousedown.stop.prevent="selectMarketplace('ME', allMarketplacesSelection)"
            @click.stop.prevent="selectMarketplace('ME', allMarketplacesSelection)"
          >
            All
          </button>
          <button
            v-for="opt in meMarketplaces"
            :key="opt.marketplaceId"
            type="button"
            class="sk-mkt-chip"
            data-region="ME"
            :data-marketplace-id="opt.marketplaceId"
            :class="{ 'is-selected': isMarketplaceSelected('ME', opt.marketplaceId) }"
            :title="opt.label"
            :aria-pressed="isMarketplaceSelected('ME', opt.marketplaceId) ? 'true' : 'false'"
            :disabled="connectingRegion !== null"
            @mousedown.stop.prevent="selectMarketplace('ME', opt.marketplaceId)"
            @click.stop.prevent="selectMarketplace('ME', opt.marketplaceId)"
          >
            {{ opt.code }}
          </button>
        </div>
        <p v-if="selectedMarketplaceLabel('ME')" class="sk-region-selected-hint">
          Selected: {{ selectedMarketplaceLabel('ME') }} — then click Authorize →
        </p>
        <div class="sk-region-card-actions">
          <button
            type="button"
            class="sk-region-authorize"
            :disabled="connectingRegion !== null"
            @click.stop.prevent="connectRegion('ME', selectedMarketplaceMe)"
          >
            {{ connectingRegion === 'ME' ? 'Opening Amazon…' : 'Authorize →' }}
          </button>
        </div>
      </div>

      <div class="sk-region-card" :class="{ 'is-busy': connectingRegion === 'IN' }">
        <div class="sk-region-card-top">
          <span class="sk-region-flag">🇮🇳</span>
          <div class="sk-region-info">
            <div class="sk-region-name">India</div>
            <div class="sk-region-countries">Amazon.in</div>
          </div>
        </div>
        <div class="sk-mkt-chips sk-mkt-chips--solo" role="group" aria-label="India marketplace">
          <button
            type="button"
            class="sk-mkt-chip sk-mkt-chip-all"
            data-region="IN"
            :data-marketplace-id="allMarketplacesSelection"
            :class="{ 'is-selected': isMarketplaceSelected('IN', allMarketplacesSelection) }"
            title="India marketplace"
            :aria-pressed="isMarketplaceSelected('IN', allMarketplacesSelection) ? 'true' : 'false'"
            :disabled="connectingRegion !== null"
            @mousedown.stop.prevent="selectMarketplace('IN', allMarketplacesSelection)"
            @click.stop.prevent="selectMarketplace('IN', allMarketplacesSelection)"
          >
            All
          </button>
          <button
            type="button"
            class="sk-mkt-chip"
            data-region="IN"
            :data-marketplace-id="inMarketplaces[0].marketplaceId"
            :class="{ 'is-selected': isMarketplaceSelected('IN', inMarketplaces[0].marketplaceId) }"
            title="India"
            :aria-pressed="isMarketplaceSelected('IN', inMarketplaces[0].marketplaceId) ? 'true' : 'false'"
            :disabled="connectingRegion !== null"
            @mousedown.stop.prevent="selectMarketplace('IN', inMarketplaces[0].marketplaceId)"
            @click.stop.prevent="selectMarketplace('IN', inMarketplaces[0].marketplaceId)"
          >
            IN
          </button>
        </div>
        <p v-if="selectedMarketplaceLabel('IN')" class="sk-region-selected-hint">
          Selected: {{ selectedMarketplaceLabel('IN') }} — then click Authorize →
        </p>
        <div class="sk-region-card-actions sk-region-card-actions--tight">
          <button
            type="button"
            class="sk-region-authorize"
            :disabled="connectingRegion !== null"
            @click.stop.prevent="connectRegion('IN', selectedMarketplaceIn)"
          >
            {{ connectingRegion === 'IN' ? 'Opening Amazon…' : 'Authorize →' }}
          </button>
        </div>
      </div>

      <div class="sk-region-card" :class="{ 'is-busy': connectingRegion === 'FE' }">
        <div class="sk-region-card-top">
          <span class="sk-region-flag">🌏</span>
          <div class="sk-region-info">
            <div class="sk-region-name">Asia Pacific</div>
            <div class="sk-region-countries">Japan, Australia, Singapore</div>
          </div>
        </div>
        <div class="sk-mkt-chips" role="group" aria-label="Asia Pacific marketplace">
          <button
            type="button"
            class="sk-mkt-chip sk-mkt-chip-all"
            data-region="FE"
            :data-marketplace-id="allMarketplacesSelection"
            :class="{ 'is-selected': isMarketplaceSelected('FE', allMarketplacesSelection) }"
            title="All Asia Pacific marketplaces"
            :aria-pressed="isMarketplaceSelected('FE', allMarketplacesSelection) ? 'true' : 'false'"
            :disabled="connectingRegion !== null"
            @mousedown.stop.prevent="selectMarketplace('FE', allMarketplacesSelection)"
            @click.stop.prevent="selectMarketplace('FE', allMarketplacesSelection)"
          >
            All
          </button>
          <button
            v-for="opt in feMarketplaces"
            :key="opt.marketplaceId"
            type="button"
            class="sk-mkt-chip"
            data-region="FE"
            :data-marketplace-id="opt.marketplaceId"
            :class="{ 'is-selected': isMarketplaceSelected('FE', opt.marketplaceId) }"
            :title="opt.label"
            :aria-pressed="isMarketplaceSelected('FE', opt.marketplaceId) ? 'true' : 'false'"
            :disabled="connectingRegion !== null"
            @mousedown.stop.prevent="selectMarketplace('FE', opt.marketplaceId)"
            @click.stop.prevent="selectMarketplace('FE', opt.marketplaceId)"
          >
            {{ opt.code }}
          </button>
        </div>
        <p v-if="selectedMarketplaceLabel('FE')" class="sk-region-selected-hint">
          Selected: {{ selectedMarketplaceLabel('FE') }} — then click Authorize →
        </p>
        <div class="sk-region-card-actions">
          <button
            type="button"
            class="sk-region-authorize"
            :disabled="connectingRegion !== null"
            @click.stop.prevent="connectRegion('FE', selectedMarketplaceFe)"
          >
            {{ connectingRegion === 'FE' ? 'Opening Amazon…' : 'Authorize →' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '../styles/signup-flow.css'
import { pushGtmEvent, markConnectStoreStarted } from '../utils/gtmEvents'
import {
  amazonAuthorizationQueryMarketplace,
  AMAZON_CONNECT_MARKETPLACES_BY_REGION,
  ALL_MARKETPLACES_SELECTION,
  findConnectMarketplaceOption,
  isAllMarketplacesSelection,
  regionAllMarketplacesLabel,
  regionMarketplaceCodes
} from '../utils/amazonSellingPartnerOAuth'

export default {
  name: 'AmazonRegionConnect',
  props: {
    showNickname: {
      type: Boolean,
      default: false
    },
    showIntro: {
      type: Boolean,
      default: false
    },
    showPricingMessage: {
      type: Boolean,
      default: false
    },
    redirectPath: {
      type: String,
      default: ''
    }
  },
  data: function () {
    var na = AMAZON_CONNECT_MARKETPLACES_BY_REGION.NA
    var eu = AMAZON_CONNECT_MARKETPLACES_BY_REGION.EU
    var me = AMAZON_CONNECT_MARKETPLACES_BY_REGION.ME
    var ind = AMAZON_CONNECT_MARKETPLACES_BY_REGION.IN
    var fe = AMAZON_CONNECT_MARKETPLACES_BY_REGION.FE
    return {
      accName: '',
      errorMsg: null,
      connectingRegion: null,
      allMarketplacesSelection: ALL_MARKETPLACES_SELECTION,
      naMarketplaces: na,
      euMarketplaces: eu,
      meMarketplaces: me,
      inMarketplaces: ind,
      feMarketplaces: fe,
      selectedMarketplaceNa: ALL_MARKETPLACES_SELECTION,
      selectedMarketplaceEu: ALL_MARKETPLACES_SELECTION,
      selectedMarketplaceMe: ALL_MARKETPLACES_SELECTION,
      selectedMarketplaceIn: ALL_MARKETPLACES_SELECTION,
      selectedMarketplaceFe: ALL_MARKETPLACES_SELECTION
    }
  },
  mounted: function () {
    var self = this
    this._onChipPick = function (e) {
      var t = e.target
      if (!t || !t.closest) return
      var chip = t.closest('.sk-mkt-chip')
      if (!chip || chip.disabled) return
      var region = chip.getAttribute('data-region')
      var id = chip.getAttribute('data-marketplace-id')
      if (!region || !id) return
      e.preventDefault()
      e.stopPropagation()
      self.selectMarketplace(region, id)
    }
    this.$el.addEventListener('mousedown', this._onChipPick, true)
    this.$el.addEventListener('click', this._onChipPick, true)
  },
  beforeDestroy: function () {
    if (!this.$el || !this._onChipPick) return
    this.$el.removeEventListener('mousedown', this._onChipPick, true)
    this.$el.removeEventListener('click', this._onChipPick, true)
  },
  methods: {
    selectedIdForRegion: function (region) {
      if (region === 'NA') return this.selectedMarketplaceNa
      if (region === 'EU') return this.selectedMarketplaceEu
      if (region === 'ME') return this.selectedMarketplaceMe
      if (region === 'IN') return this.selectedMarketplaceIn
      if (region === 'FE') return this.selectedMarketplaceFe
      return null
    },
    isMarketplaceSelected: function (region, marketplaceId) {
      return String(this.selectedIdForRegion(region) || '') === String(marketplaceId || '')
    },
    selectedMarketplaceLabel: function (region) {
      var id = this.selectedIdForRegion(region)
      if (isAllMarketplacesSelection(id)) return regionAllMarketplacesLabel(region)
      var opt = findConnectMarketplaceOption(region, id)
      return opt ? opt.label : ''
    },
    selectMarketplace: function (region, marketplaceId) {
      if (this.connectingRegion) return
      var id = String(marketplaceId)
      if (region === 'NA') this.selectedMarketplaceNa = id
      else if (region === 'EU') this.selectedMarketplaceEu = id
      else if (region === 'ME') this.selectedMarketplaceMe = id
      else if (region === 'IN') this.selectedMarketplaceIn = id
      else if (region === 'FE') this.selectedMarketplaceFe = id
    },
    connectRegion: function (region, marketplaceId) {
      var self = this
      self.errorMsg = null
      self.connectingRegion = region
      var path = self.redirectPath || (self.$route && self.$route.path) || '/settings/account'
      var allInRegion = isAllMarketplacesSelection(marketplaceId)
      var opt = findConnectMarketplaceOption(region, marketplaceId)
      var data = {
        region: region,
        path: path,
        accName: self.accName
      }
      if (allInRegion) {
        data.marketplace = amazonAuthorizationQueryMarketplace(region)
        data.allMarketplaces = true
        var codes = regionMarketplaceCodes(region)
        if (codes.length) {
          data.marketplaceCodes = codes.join(',')
          data.marketplaceCode = codes.join(',')
        }
      } else {
        data.marketplace = amazonAuthorizationQueryMarketplace(region, marketplaceId)
        data.marketplaceId = marketplaceId
        if (opt && opt.code) {
          data.marketplaceCode = opt.code
          data.marketplaceIso31662Code = opt.code.toLowerCase()
        }
      }
      // Spec: fire when user clicks Authorize — wait for GTM before Amazon redirect
      markConnectStoreStarted()
      var amazonUrl = null
      var gtmDone = false
      function goAmazonWhenReady () {
        if (!amazonUrl || !gtmDone) return
        window.location = amazonUrl
      }
      pushGtmEvent({ event: 'connect_store_started', step: 2 }, function () {
        gtmDone = true
        goAmazonWhenReady()
      })
      self.$http.get('/api/identity/amazon/authorization/redirect', { params: data }).then(
        function (response) {
          amazonUrl = response.body.location
          goAmazonWhenReady()
        },
        function (response) {
          self.connectingRegion = null
          var msg = (response.body && response.body.message) || 'Failed to start authorization'
          self.errorMsg = msg
          self.$emit('error', msg)
          pushGtmEvent({ event: 'connect_store_failed', error_reason: msg })
        }
      )
    }
  }
}
</script>

<style scoped>
.amazon-region-connect {
  font-family: 'DM Sans', -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.sk-seller-nickname {
  margin-bottom: 20px;
}

.sk-seller-nickname input {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1.5px solid var(--sk-border-strong, #d1d9e6);
  border-radius: 8px;
  font-family: inherit;
  box-sizing: border-box;
}

.sk-seller-nickname input:focus {
  outline: none;
  border-color: var(--sk-orange, #f5841f);
  box-shadow: 0 0 0 3px var(--sk-orange-dim, rgba(245, 132, 31, 0.15));
}

.sk-optional {
  font-weight: 400;
  color: var(--sk-text-dim, #94a3b8);
}

.sk-pricing-note {
  font-size: 13px;
  color: var(--sk-text-mid, #64748b);
  margin: 0 0 16px;
  line-height: 1.45;
}

.sk-error-msg {
  margin-bottom: 16px;
}

.amazon-region-connect .sk-region-grid {
  margin-bottom: 0;
}

.sk-region-selected-hint {
  margin: 0 0 8px;
  padding-left: 34px;
  font-size: 12px;
  color: var(--sk-text-mid, #64748b);
}

.sk-region-selected-hint--tight {
  padding-left: 0;
  margin-top: 4px;
}

.sk-mkt-chip-all {
  min-width: 48px;
  font-weight: 700;
}

.sk-mkt-chips--solo {
  margin-top: 4px;
}

.amazon-region-connect .sk-mkt-chip {
  position: relative;
  z-index: 2;
  pointer-events: auto;
}

.amazon-region-connect .sk-mkt-chip.is-selected,
.ui.modal .amazon-region-connect .sk-mkt-chip.is-selected {
  border-color: #f5841f !important;
  color: #f5841f !important;
  background: rgba(245, 132, 31, 0.12) !important;
}

.ui.modal .amazon-region-connect .sk-region-authorize {
  color: var(--sk-orange, #f5841f) !important;
}
</style>



// WEBPACK FOOTER //
// AmazonRegionConnect.vue?48cdb057